import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { API } from "../../../http-common";
import { BUSINESS, getBusinessName, ROLE } from "../../../helpers/constants";

const ROLES_REQUIRING_DIRECTOR = [
  ROLE.EQ_COMERCIAL,
  ROLE.EQ_COMERCIAL_ASISTENTE,
  ROLE.EQ_COMERCIAL_VIEWER,
  ROLE.EQ_TEAM_LEADER,
];

export const MoveBusinessModal = ({ isOpen, toggle, user, onMoved }) => {
  const currentBizId = user?.business_id ?? null;
  const requiresDirector = ROLES_REQUIRING_DIRECTOR.includes(user?.role_id);

  const [selectedBizId, setSelectedBizId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [directorOptions, setDirectorOptions] = useState([]);
  const [selectedDirectorId, setSelectedDirectorId] = useState("");
  const [directorsLoading, setDirectorsLoading] = useState(false);

  const businessOptions = useMemo(() => {
    const all = [
      BUSINESS.IFA,
      BUSINESS.PRODUCTORES_EXCLUSIVOS,
      BUSINESS.CANALES,
      BUSINESS.START
    ];
    if (currentBizId == null) return [];
    return all.filter((id) => Number(id) !== Number(currentBizId));
  }, [currentBizId]);

  useEffect(() => {
    if (!isOpen) return;
    setErrorMsg("");
    setLoading(false);
    setSelectedBizId(null);
    setSelectedDirectorId("");
    setDirectorOptions([]);
  }, [isOpen]);

  useEffect(() => {
    setSelectedDirectorId("");

    if (!isOpen || !requiresDirector || selectedBizId == null) {
      setDirectorOptions([]);
      return;
    }

    let alive = true;

    const loadDirectors = async () => {
      setDirectorsLoading(true);
      try {
        const resp = await API.get(
          `/user/directors_by_business/${selectedBizId}`
        );
        const data = Array.isArray(resp?.data?.data) ? resp.data.data : [];
        if (alive) setDirectorOptions(data);
      } catch (error) {
        if (alive) setDirectorOptions([]);
      } finally {
        if (alive) setDirectorsLoading(false);
      }
    };

    loadDirectors();

    return () => {
      alive = false;
    };
  }, [isOpen, requiresDirector, selectedBizId]);

  const noDirectorsAvailable =
    requiresDirector &&
    selectedBizId != null &&
    !directorsLoading &&
    directorOptions.length === 0;

  const canSubmit =
    user?.id != null &&
    selectedBizId != null &&
    (!requiresDirector || (!!selectedDirectorId && !noDirectorsAvailable)) &&
    !loading;

  const handleSubmit = async () => {
    if (!user?.id) {
      setErrorMsg("No se pudo cargar el usuario.");
      return;
    }

    if (selectedBizId == null) {
      setErrorMsg("Debe seleccionar una unidad de negocio destino.");
      return;
    }

    if (requiresDirector && noDirectorsAvailable) {
      setErrorMsg("No hay directores disponibles en la unidad de negocio seleccionada.");
      return;
    }

    if (requiresDirector && !selectedDirectorId) {
      setErrorMsg("Debe seleccionar un director para este rol en la nueva unidad de negocio.");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      await API.patch(`/user/${user.id}/business`, {
        business_id: Number(selectedBizId),
        ...(requiresDirector
          ? { relation_user_id: Number(selectedDirectorId) }
          : {}),
      });

      onMoved?.();
      toggle();
    } catch (error) {
      setErrorMsg(
        error?.response?.data?.errors?.[0]?.msg ||
          error?.response?.data?.message ||
          "Error al mover la unidad de negocio.",
      );
    } finally {
      setLoading(false);
    }
  };

  const currentBizName =
    currentBizId != null ? getBusinessName(currentBizId) : "Cargando...";

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Mover usuario a otra unidad de negocio</ModalHeader>

      <ModalBody>
        <p style={{ marginBottom: 6 }}>
          Usted está en la unidad de negocio <b>{currentBizName}</b>
        </p>
        <p
          style={{ marginTop: 0, marginBottom: 16, fontSize: 13, opacity: 0.8 }}
        >
          Seleccioná donde querés mover al usuario
        </p>

        {!user || currentBizId == null ? (
          <p style={{ marginTop: 10, fontSize: 14, opacity: 0.8 }}>
            Cargando datos del usuario...
          </p>
        ) : (
          <FormGroup>
            {businessOptions.map((bizId) => (
              <div
                key={bizId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                <Input
                  type="radio"
                  name="move_business"
                  id={`move-business-${bizId}`}
                  checked={Number(selectedBizId) === Number(bizId)}
                  onChange={() => {
                    setSelectedBizId(bizId);
                    setErrorMsg("");
                  }}
                />
                <Label
                  htmlFor={`move-business-${bizId}`}
                  style={{ margin: 0, cursor: "pointer" }}
                >
                  {getBusinessName(bizId)}
                </Label>
              </div>
            ))}
          </FormGroup>
        )}

        {requiresDirector && selectedBizId != null && (
          <FormGroup style={{ marginTop: 10 }}>
            <p style={{ marginTop: 0, marginBottom: 8, fontSize: 13, opacity: 0.8 }}>
              Dado que este rol requiere un director, seleccionar cual tendrá en la nueva unidad de negocio.
            </p>
            <Label>Director en {getBusinessName(selectedBizId)}</Label>
            <Input
              type="select"
              value={selectedDirectorId}
              onChange={(e) => {
                setSelectedDirectorId(e.target.value);
                setErrorMsg("");
              }}
              disabled={directorsLoading || noDirectorsAvailable}
            >
              <option value="">
                {directorsLoading ? "Cargando directores..." : "Seleccione un director"}
              </option>
              {directorOptions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.username} ({d.email})
                </option>
              ))}
            </Input>
            {noDirectorsAvailable && (
              <p style={{ marginTop: 6, fontSize: 13, color: "red" }}>
                No hay directores disponibles en esta unidad de negocio. No es posible mover al usuario hasta que exista al menos un director.
              </p>
            )}
          </FormGroup>
        )}

        {errorMsg && (
          <p style={{ color: "red", marginTop: 10, fontSize: 14 }}>
            {errorMsg}
          </p>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Cancelar
        </Button>

        <Button
          color="primary"
          style={{ background: "#24589c" }}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {loading ? "Moviendo..." : "Confirmar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
